import type { api } from "@healthos/schemas";
import { announceRouteMilestone } from "../route-milestone-announcer";

/**
 * Dev-only preview of the route milestone celebration.
 *
 * Guarded by `import.meta.env.DEV` so this placeholder route can never surface
 * to a member (same rule as `lib/widget-dev.ts` on mobile). It exists so the
 * modal and its motion can be reviewed without walking a real route to a
 * waypoint, and it never touches the API, so nothing is claimed or posted.
 */

/** A coast-to-coast shape, enough points to read as a real route at card size. */
const PREVIEW_PATH = Array.from({ length: 40 }, (_, index) => {
  const t = index / 39;
  return {
    lat: 50.1 + t * 8.4 + Math.sin(t * 9) * 0.32,
    lng: -5.2 + t * 2.1 + Math.cos(t * 7) * 0.45,
  };
});

const PREVIEW_MILESTONES = [
  { fraction: 0.2, title: "Bristol Channel", description: "The first coastline behind you." },
  { fraction: 0.45, title: "The Peak District", description: "Higher ground, longer views." },
  { fraction: 0.68, title: "Hadrian's Wall", description: "Two thousand years of footsteps." },
  { fraction: 0.9, title: "The Cairngorms", description: "The last climb before the coast." },
];

export function previewRouteMilestone(
  subject: "member" | "group",
  kind: api.RouteMilestoneKind = "waypoint",
): void {
  if (!import.meta.env.DEV) return;
  const isGroup = subject === "group";
  const isFinish = kind === "completion";
  const celebration: api.RouteMilestoneCelebration = {
    kind,
    instanceId: "00000000-0000-4000-8000-0000000000de",
    enrollmentId: "00000000-0000-4000-8000-000000000001",
    routeName: "Length of Britain",
    milestoneIndex: isFinish ? PREVIEW_MILESTONES.length - 1 : 2,
    milestoneTitle: isFinish ? "The finish line" : "Hadrian's Wall",
    milestoneDescription: isFinish
      ? "Every ordinary day of movement added up to the whole route."
      : "Two thousand years of footsteps.",
    milestoneFraction: isFinish ? 1 : 0.68,
    milestoneOrdinal: isFinish ? PREVIEW_MILESTONES.length : 3,
    milestoneCount: PREVIEW_MILESTONES.length,
    progressFraction: isFinish ? 1 : 0.71,
    distanceMeters: 1_407_000,
    routePath: PREVIEW_PATH,
    routeMilestones: PREVIEW_MILESTONES,
    subject,
    groupId: isGroup ? "00000000-0000-4000-8000-000000000002" : null,
    groupName: isGroup ? "Morning Momentum" : null,
    memberCount: isGroup ? 6 : null,
  };
  announceRouteMilestone(celebration, { preview: true });
}
